
import PrimaryButton from '@/components/shared/PrimaryButton';
import AutoAskOptionEmailIcon from '@/components/finagent/icons/AutoAskOptionEmailIcon';
import AutoAskOptionPlatfomIcon from '@/components/finagent/icons/AutoAskOptionPlatfomIcon';
import CustomAutoAskQuestionFrequency from '@/components/finagent/CustomAutoAskQuestionFrequency';
import FinAgentService from '@/service/FinAgentService';
import FormatterService from '@/service/FormatterService';
import { backendExceptionHandler } from '@/mixins';
import debounce from 'lodash/debounce';
import { frequencyValues, repeatUnitValues, daysOfWeekValues, endsTypeValues } from '@/constants/finAgent';

const today = new Date();
const defaultEndsOnDate = new Date(today.setDate(today.getDate() + 1)); // defaut 1 day from today
const defaultCustomFrequency = {
    frequencyInterval: 1,
    frequencyUnit: repeatUnitValues.day, // Default to "Day"
    frequencyWeekday: daysOfWeekValues.mon, // Default to Monday
    frequencyEndMode: endsTypeValues.never,
    frequencyEndDate: defaultEndsOnDate.toISOString().slice(0, 10),
    frequencyEndOccurrences: 10,
};

export default {
    name: 'FinAgentAutoAskQuestionModal',

    components: {
        PrimaryButton,
        AutoAskOptionEmailIcon,
        AutoAskOptionPlatfomIcon,
        CustomAutoAskQuestionFrequency,
    },

    mixins: [backendExceptionHandler],

    props: {
        value: {
            type: Boolean,
            default: false,
        },
        recurringId: {
            type: [Number, String],
            default: null,
        },
        defaultFinagentThreadMessageId: {
            type: [Number, String],
            default: null,
        },
        defaultQuestion: {
            type: String,
            default: '',
        },
        defaultFrequency: {
            type: String,
            default: '',
        },
        defaultTitle: {
            type: String,
            default: '',
        },
    },

    emits: ['input', 'updated-success', 'created-success'],

    data() {
        return {
            title: '',
            question: '',
            finagentThreadMessageId: null,
            isSubmitting: false,
            errorMessage: '',
            deliveryPathEmail: false,
            deliveryPathNotificationCenter: true,
            showErrorFrequency: false,
            selectedFrequency: frequencyValues.daily,
            customFrequency: defaultCustomFrequency,
            errorFrequencyInterval: '',
            errorFrequencyEndOccurrences: '',
            isLoadingAutoAskQuestion: false,
            showTimePicker: false,
            concreteTime: '00:00',
            questionData: {},
        }
    },

    computed: {
        backIconPath() {
            return this.$vuetify.theme.dark ?
                require('@/assets/icons/back-dark.svg') :
                require('@/assets/icons/back-light.svg');
        },
        submitDisabled() {
            const customFreqInvalid =
                this.selectedFrequency === this.frequencyValues.custom &&
                (!!this.errorFrequencyInterval || !!this.errorFrequencyEndOccurrences);
            const notificationPreferencesInvalid = !this.deliveryPathEmail && !this.deliveryPathNotificationCenter;

            return !this.title.trim() ||
                !this.question.trim() ||
                notificationPreferencesInvalid ||
                !this.selectedFrequency ||
                customFreqInvalid ||
                this.isSubmitting ||
                this.isLoadingAutoAskQuestion;
        },
        isCreateMode() {
            return !this.recurringId;
        },
        showErrorNotificationPreferences() {
            return !this.deliveryPathEmail && !this.deliveryPathNotificationCenter;
        }
    },

    watch: {
        value(isOpened) {
            if (isOpened) {
                if (this.isCreateMode) {
                    this.title = this.defaultTitle || '';
                    this.selectedFrequency = this.defaultFrequency || frequencyValues.daily;
                    this.question = this.defaultQuestion || '';
                    this.finagentThreadMessageId = this.defaultFinagentThreadMessageId || null;
                    return;
                }

                this.fetchAutoAskQuestion();
                return;
            }

            this.reset();
            this.cancelActiveRequest();
        },
    },

    created() {
        this.activeFetchDetailsRequest = null;
        this.finAgentService = new FinAgentService(this.$axios);
        this.formatter = new FormatterService();
        this.rules = {
            titleRequired: v => !!v?.trim() || this.$t('components.finAgentAutoAskQuestion.titleRequired'),
            questionRequired: v => !!v?.trim() || this.$t('components.finAgentAutoAskQuestion.questionRequired'),
        }
        this.debouncedSubmit = debounce(this.submit, 300);
        this.frequencyValues = frequencyValues;
        this.frequencyOptions = [
            {
                value: frequencyValues.daily,
                label: this.$t('components.finAgentAutoAskQuestion.frequencyOptions.daily')
            },
            {
                value: frequencyValues.weekly,
                label: this.$t('components.finAgentAutoAskQuestion.frequencyOptions.weekly')
            },
            {
                value: frequencyValues.monthly,
                label: this.$t('components.finAgentAutoAskQuestion.frequencyOptions.monthly')
            },
            {
                value: frequencyValues.quarterly,
                label: this.$t('components.finAgentAutoAskQuestion.frequencyOptions.quarterly')
            },
            {
                value: frequencyValues.yearly,
                label: this.$t('components.finAgentAutoAskQuestion.frequencyOptions.yearly')
            },
            {
                value: frequencyValues.custom,
                label: this.$t('components.finAgentAutoAskQuestion.frequencyOptions.custom')
            },
        ];
    },

    methods: {
        cancelActiveRequest() {
            if (this.activeFetchDetailsRequest) {
                this.activeFetchDetailsRequest.cancel();
                this.activeFetchDetailsRequest = null;
            }
        },

        reset() {
            this.title = '';
            this.question = '';
            this.finagentThreadMessageId = null;
            this.isSubmitting = false;
            this.errorMessage = '';
            this.showErrorFrequency = false;
            this.selectedFrequency = frequencyValues.daily;
            this.deliveryPathEmail = false;
            this.deliveryPathNotificationCenter = true;
            this.customFrequency = { ...defaultCustomFrequency };
            this.errorFrequencyInterval = '';
            this.errorFrequencyEndOccurrences = '';
            this.isLoadingAutoAskQuestion = false;
            this.concreteTime = '00:00';
            this.showTimePicker = false;
            this.questionData = {};
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        closeDialog() {
            this.$emit('input', false);
        },

        getCustomFrequencyDataToSubmit() {
            const data ={
                frequencyInterval: Number(this.customFrequency.frequencyInterval),
                frequencyUnit: this.customFrequency.frequencyUnit,
                frequencyWeekday: this.customFrequency.frequencyWeekday,
                frequencyEndMode: this.customFrequency.frequencyEndMode,
                frequencyEndDate: `${this.customFrequency.frequencyEndDate}T00:00:00+00:00`, // Ensure date is in ISO format
                frequencyEndOccurrences: Number(this.customFrequency.frequencyEndOccurrences),
            };

            if (data.frequencyEndMode === endsTypeValues.never) {
                data.frequencyEndDate = null;
                data.frequencyEndOccurrences = null;
            } else if (data.frequencyEndMode === endsTypeValues.on) {
                data.frequencyEndOccurrences = null;
            } else if (data.frequencyEndMode === endsTypeValues.after) {
                data.frequencyEndDate = null;
            }

            return data;
        },

        getDataToSubmit() {
            const customFrequencyData = this.getCustomFrequencyDataToSubmit();
            const data = {
                isEnabled: this.questionData.isEnabled ?? true,
                title: this.title,
                question: this.question,
                deliveryPathEmail: this.deliveryPathEmail,
                deliveryPathNotificationCenter: this.deliveryPathNotificationCenter,
                frequency: this.selectedFrequency,
                frequencyTime: this.formatter.getUtcTimeFromLocalTime(this.concreteTime),
                ...customFrequencyData
            };
            return data;
        },

        parseCustomFrequencyData(originAutoAskQuestion) {            
            return {
                frequencyInterval: originAutoAskQuestion.frequencyInterval,
                frequencyUnit: originAutoAskQuestion.frequencyUnit,
                frequencyWeekday: originAutoAskQuestion.frequencyWeekday,
                frequencyEndMode: originAutoAskQuestion.frequencyEndMode,
                frequencyEndDate: originAutoAskQuestion.frequencyEndDate
                    ? originAutoAskQuestion.frequencyEndDate.slice(0, 10)
                    : null,
                frequencyEndOccurrences: originAutoAskQuestion.frequencyEndOccurrences,
            };
        },

        async fetchAutoAskQuestion() {
            if (!this.recurringId) return;
            const axiosSource = this.$axios.CancelToken.source()
            this.activeFetchDetailsRequest = { cancel: axiosSource.cancel, msg: "Cancel get detail auto-ask question..." }
            this.isLoadingAutoAskQuestion = true;
            try {
                const questionData = await this.finAgentService.getAutoAskQuestion(this.recurringId, axiosSource);
                this.questionData = questionData;
                this.title = questionData.title;
                this.question = questionData.question;
                this.deliveryPathEmail = questionData.deliveryPathEmail ?? false;
                this.deliveryPathNotificationCenter = questionData.deliveryPathNotificationCenter ?? false;
                this.selectedFrequency = questionData.frequency;
                this.finagentThreadMessageId = questionData.finagentThreadMessageId || null;
                this.concreteTime = this.formatter.getLocalTimeFromUtcTime(questionData.frequencyTime);
                this.customFrequency = questionData.frequency === frequencyValues.custom
                    ? this.parseCustomFrequencyData(questionData)
                    : defaultCustomFrequency;                            
            } catch (error) {
                if (this.$axios.isCancel(error)) {
                    return;
                }
                this.handleBackendException(error, this.$t('components.finAgentAutoAskQuestion.fetchError'));
            } finally {
                this.isLoadingAutoAskQuestion = false;
                this.activeFetchDetailsRequest = null;
            }
        },

        async createAutoAskQuestion() {
            this.isSubmitting = true;
            const payload = this.getDataToSubmit();

            try {
                const createdQuestion = await this.finAgentService.createAutoAskQuestion(payload);
                this.$emit('created-success', createdQuestion);
                
                this.$store.commit('snackbar/updateType', 'success');
                this.$store.commit('snackbar/updateMessage', this.$t('components.finAgentAutoAskQuestion.createdSuccess'));
                this.closeDialog();
            } catch (error) {
                this.handleBackendException(error, this.$t('components.finAgentAutoAskQuestion.createdError'));
                this.$store.commit('snackbar/updateType', 'error');
                this.$store.commit('snackbar/updateMessage', this.errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },

        async updateAutoAskQuestion() {
            this.isSubmitting = true;
            const payload = this.getDataToSubmit();

            try {
                const updatedQuestion = await this.finAgentService.updateAutoAskQuestion(this.recurringId, payload);
                this.$emit('updated-success', updatedQuestion);

                this.$store.commit('snackbar/updateType', 'success');
                this.$store.commit('snackbar/updateMessage', this.$t('components.finAgentAutoAskQuestion.updatedSuccess'));
                this.closeDialog();
            } catch (error) {
                this.handleBackendException(error, this.$t('components.finAgentAutoAskQuestion.updatedError'))
                this.$store.commit('snackbar/updateType', 'error');
                this.$store.commit('snackbar/updateMessage', this.errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },

        validateFrequency() {
            if (!this.selectedFrequency) {
                this.showErrorFrequency = true;
                return false;
            }
            this.showErrorFrequency = false;
            return true;
        },

        handleCustomFrequencyValidation({ errorFrequencyInterval, errorFrequencyEndOccurrences }) {
            this.errorFrequencyInterval = errorFrequencyInterval;
            this.errorFrequencyEndOccurrences = errorFrequencyEndOccurrences;
        },

        validateFormData() {
            const notificationPreferencesValid = this.deliveryPathEmail || this.deliveryPathNotificationCenter;
            const frequencyValid = this.validateFrequency();
            const titleValid = this.title.trim() !== '';
            const questionValid = this.question.trim() !== '';

            return notificationPreferencesValid && frequencyValid && titleValid && questionValid;
        },

        async submit() {
            if (this.submitDisabled) return;

            if (!this.$refs.form.validate()) {
                return;
            }

            if (!this.validateFormData()) {
                return;
            }

            if (this.isCreateMode) {
                await this.createAutoAskQuestion();
            } else {
                await this.updateAutoAskQuestion();
            }
        },

        handleClickFrequence(value) {
            if (this.selectedFrequency === value) {
                return;
            }
            
            this.selectedFrequency = value;
            this.showErrorFrequency = !this.selectedFrequency;
        },
    },

}
